import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import SystemDialog, { type DialogCheckedState } from "../../components/UIToolkit/Popups/Dialog";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { INK, NEUTRAL, STATUS } from "../../theme/tokens";
import { formatNumber } from "../utilFunctions";
import { buildEmployeeStatusWarning, type EmployeeStatusWarningProps } from "./employeeStatusWarning";
import { useDismissOverstaffingWarning } from "./useDismissOverstaffingWarning";
import { DIFF_HOURS_WARNING, isAgencyNurse } from "./utils";

/**
 * Structured body for the recurring daily-hours warning: a lead sentence over a
 * scannable, scrollable card of the exact over-limit dates as amber pills —
 * instead of a wall of newline-joined text. Generic (any date-string list); used
 * wherever a recurring add produces many affected dates (master-schedule rotation
 * editor, per-week editor). Non-recurring adds keep the single-sentence body.
 */
const DailyHoursWarningBody: React.FC<{ count: number; limit: number; dates: string[] }> = ({ count, limit, dates }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
				<WarningAmberRoundedIcon sx={{ color: STATUS.WARNING.main, fontSize: 20, flexShrink: 0, mt: "1px" }} />
				<Typography sx={{ color: INK.SECONDARY, fontSize: 14, lineHeight: 1.5 }}>{t("daily.hours.warning.recurring.body", { count, limit })}</Typography>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
				<Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: INK.MUTED }}>
					{t("daily.hours.warning.affectedDates", { count })}
				</Typography>
				<Box
					data-testid="daily-hours-warning-dates"
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 0.75,
						maxHeight: 208,
						overflowY: "auto",
						p: 1.25,
						borderRadius: 2,
						border: `1px solid ${NEUTRAL[200]}`,
						backgroundColor: NEUTRAL[50],
					}}
				>
					{dates.map((d) => (
						<Box
							key={d}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.625,
								px: 1,
								py: 0.375,
								borderRadius: 999,
								backgroundColor: STATUS.WARNING.light,
								color: STATUS.WARNING.dark,
								fontSize: 12.5,
								fontWeight: 600,
								whiteSpace: "nowrap",
							}}
						>
							<Box aria-hidden sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: STATUS.WARNING.main, flexShrink: 0 }} />
							{d}
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

// Re-exported for the many existing `import { isAgencyNurse } from ".../WarningDialog"` call sites.
// Re-exported for the existing `import { buildEmployeeStatusWarning } from ".../WarningDialog"` call sites.
export { buildEmployeeStatusWarning, type EmployeeStatusWarningProps, isAgencyNurse };

export interface OvertimeWarningProps {
	hours: number;
	cost: string;
}

export interface DailyHoursWarningProps {
	hours: number;
	dates?: string[];
}

/** `current` is the projected staffed count INCLUDING the nurse being added; `target` the location's required total. */
export interface TargetWarningProps {
	current: number;
	target: number;
	location: string;
}

export interface NurseWarningState {
	isTargetWarningOpen: boolean;
	isPtoWarningOpen: boolean;
	isAgencyWarningOpen: boolean;
	isOvertimeWarningOpen: boolean;
	isDailyHoursWarningOpen: boolean;
	isEmployeeStatusWarningOpen: boolean;
	overtimeDetails: OvertimeWarningProps;
	dailyHoursDetails: DailyHoursWarningProps;
	targetDetails: TargetWarningProps;
	employeeStatusDetails: EmployeeStatusWarningProps | null;
}

export interface WarningDialogProps {
	openTargetWarning?: boolean;
	openPtoWarning?: boolean;
	openAgencyWarning: boolean;
	openOvertimeWarning: boolean;
	openDailyHoursWarning: boolean;
	openEmployeeStatusWarning?: boolean;
	overtimeDetails: OvertimeWarningProps;
	dailyHoursDetails: DailyHoursWarningProps;
	targetDetails?: TargetWarningProps;
	employeeStatusDetails?: EmployeeStatusWarningProps | null;
	longShiftDuration?: number;
	zIndex?: number;
	onCancelTarget?: () => void;
	onConfirmTarget?: () => void;
	onCancelPto?: () => void;
	onConfirmPto?: () => void;
	onCancelAgency: () => void;
	onConfirmAgency: () => void;
	onCancelOvertime: () => void;
	onConfirmOvertime: () => void;
	onCancelDailyHours: () => void;
	onConfirmDailyHours: () => void;
	onCancelEmployeeStatus?: () => void;
	onConfirmEmployeeStatus?: () => void;
}

export const WarningDialog: React.FC<WarningDialogProps> = ({
	openTargetWarning = false,
	openPtoWarning = false,
	openAgencyWarning,
	openOvertimeWarning,
	openDailyHoursWarning,
	openEmployeeStatusWarning,
	overtimeDetails,
	dailyHoursDetails,
	targetDetails = { current: 0, target: 0, location: "" },
	employeeStatusDetails,
	longShiftDuration,
	zIndex = 1400,
	onCancelTarget = () => {},
	onConfirmTarget = () => {},
	onCancelPto = () => {},
	onConfirmPto = () => {},
	onCancelAgency,
	onConfirmAgency,
	onCancelOvertime,
	onConfirmOvertime,
	onCancelDailyHours,
	onConfirmDailyHours,
	onCancelEmployeeStatus,
	onConfirmEmployeeStatus,
}) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const limit = longShiftDuration ?? DIFF_HOURS_WARNING;
	const dismissOverstaffingWarning = useDismissOverstaffingWarning();
	const dontShowAgainLabel = t("staffing.target.warning.dont.show.again");

	const handleConfirmTarget = (checkedState?: DialogCheckedState[]) => {
		if (checkedState?.find((c) => c.label === dontShowAgainLabel)?.checked) {
			dismissOverstaffingWarning.mutate();
		}
		onConfirmTarget();
	};

	return (
		<>
			{openEmployeeStatusWarning && employeeStatusDetails && onConfirmEmployeeStatus && onCancelEmployeeStatus && (
				<SystemDialog
					dataTestId="employee-status-add-warning"
					open
					onClose={onCancelEmployeeStatus}
					title={t("employee.status.warn.title")}
					body={t(
						employeeStatusDetails.status === "Suspended"
							? employeeStatusDetails.endDate
								? "employee.status.warn.suspended.until"
								: "employee.status.warn.suspended.open"
							: employeeStatusDetails.endDate
								? "employee.status.warn.on_leave.until"
								: "employee.status.warn.on_leave.open",
						{ name: formatPersonName(employeeStatusDetails.person), date: employeeStatusDetails.endDate ?? undefined },
					)}
					onConfirm={onConfirmEmployeeStatus}
					cancel={t("cancel")}
					confirm={t("employee.status.warn.add_anyway")}
					zIndex={zIndex}
				/>
			)}

			<SystemDialog
				dataTestId="staffing-target-warning"
				open={openTargetWarning}
				onClose={onCancelTarget}
				title={t("staffing.target.warning")}
				body={t("staffing.target.warning.body", { current: targetDetails.current, target: targetDetails.target, location: targetDetails.location })}
				onConfirm={handleConfirmTarget}
				checkboxes={[{ label: dontShowAgainLabel, required: false, defaultValue: false }]}
				zIndex={zIndex}
			/>

			<SystemDialog
				dataTestId="pto-warning"
				open={openPtoWarning}
				onClose={onCancelPto}
				title={t("pto.warning")}
				body={t("pto.warning.body")}
				onConfirm={onConfirmPto}
				zIndex={zIndex}
			/>

			<SystemDialog
				open={openAgencyWarning}
				onClose={onCancelAgency}
				title={t("agency.nurse.warning")}
				body={t("agency.nurse.warning.body")}
				onConfirm={onConfirmAgency}
				zIndex={zIndex}
			/>

			<SystemDialog
				dataTestId="overtime-warning"
				open={openOvertimeWarning}
				onClose={onCancelOvertime}
				title={t("overtime.warning")}
				body={t("overtime.warning.body", { overtimeHours: overtimeDetails.hours, overtimeCost: overtimeDetails.cost })}
				onConfirm={onConfirmOvertime}
				zIndex={zIndex}
			/>

			<SystemDialog
				dataTestId="daily-hours-warning"
				open={openDailyHoursWarning}
				onClose={onCancelDailyHours}
				title={t("daily.hours.warning")}
				body={
					dailyHoursDetails.dates?.length ? (
						<DailyHoursWarningBody count={dailyHoursDetails.dates.length} limit={limit} dates={dailyHoursDetails.dates} />
					) : (
						t("daily.hours.warning.body", { dailyHours: formatNumber(dailyHoursDetails.hours), limit })
					)
				}
				onConfirm={onConfirmDailyHours}
				zIndex={zIndex}
			/>
		</>
	);
};

type PendingCascade = { isAgency: boolean; overtimeHours: number; dailyHours: number; isActual: boolean; hasPto: boolean; targetExceeded: boolean };

export const useNurseWarnings = (
	onComplete: () => void,
	longShiftDuration?: number,
): [
	NurseWarningState,
	(
		isAgency: boolean,
		overtimeHours?: number,
		overtimeCost?: string,
		dailyHours?: number,
		isActual?: boolean,
		hasPto?: boolean,
		targetWarning?: { exceeded: boolean; current: number; target: number; location: string },
		employeeStatusWarning?: EmployeeStatusWarningProps,
	) => void,
	() => void,
	() => void,
	() => void,
	() => void,
	() => void,
	() => void,
	() => void,
] => {
	const limit = longShiftDuration ?? DIFF_HOURS_WARNING;
	const [warningState, setWarningState] = React.useState<NurseWarningState>({
		isTargetWarningOpen: false,
		isPtoWarningOpen: false,
		isAgencyWarningOpen: false,
		isOvertimeWarningOpen: false,
		isDailyHoursWarningOpen: false,
		isEmployeeStatusWarningOpen: false,
		overtimeDetails: { hours: 0, cost: "$0" },
		dailyHoursDetails: { hours: 0 },
		targetDetails: { current: 0, target: 0, location: "" },
		employeeStatusDetails: null,
	});
	// Inputs for the rest of the cascade, parked while a front-gate warning (employee status / target) is shown.
	const pendingRef = React.useRef<PendingCascade | null>(null);

	/** Determine next warning after PTO in the cascade: daily hours -> overtime -> complete */
	const proceedAfterPto = (state: NurseWarningState) => {
		if (state.dailyHoursDetails.hours > limit) {
			setWarningState((prev) => ({
				...prev,
				isPtoWarningOpen: false,
				isDailyHoursWarningOpen: true,
			}));
		} else if (state.overtimeDetails.hours > 0) {
			setWarningState((prev) => ({
				...prev,
				isPtoWarningOpen: false,
				isOvertimeWarningOpen: true,
			}));
		} else {
			setWarningState((prev) => ({ ...prev, isPtoWarningOpen: false }));
			onComplete();
		}
	};

	/** PTO -> daily hours -> overtime -> complete. Agency/actual adds skip straight to complete. */
	const proceedStandardCascade = (state: NurseWarningState, { isAgency, overtimeHours, dailyHours, isActual, hasPto }: PendingCascade) => {
		const base: NurseWarningState = { ...state, isTargetWarningOpen: false };
		if (isActual || isAgency) {
			setWarningState(base);
			onComplete();
		} else if (hasPto) {
			setWarningState({ ...base, isPtoWarningOpen: true });
		} else if (dailyHours > limit) {
			setWarningState({ ...base, isDailyHoursWarningOpen: true });
		} else if (overtimeHours > 0) {
			setWarningState({ ...base, isOvertimeWarningOpen: true });
		} else {
			setWarningState(base);
			onComplete();
		}
	};

	/** Staffing-target gate -> standard cascade. Reached directly, or after the employee-status gate is confirmed. */
	const proceedToTargetGate = (state: NurseWarningState, cascade: PendingCascade) => {
		// Exceeding the staffing target gates the rest of the cascade — but only for scheduled adds,
		// not actual worked-time entries (where coverage targets don't apply).
		if (!cascade.isActual && cascade.targetExceeded) {
			pendingRef.current = cascade;
			setWarningState({ ...state, isEmployeeStatusWarningOpen: false, isTargetWarningOpen: true });
			return;
		}
		pendingRef.current = null;
		proceedStandardCascade({ ...state, isEmployeeStatusWarningOpen: false }, cascade);
	};

	const checkWarnings = (
		isAgency: boolean,
		overtimeHours?: number,
		overtimeCost?: string,
		dailyHours?: number,
		isActual?: boolean,
		hasPto?: boolean,
		targetWarning?: { exceeded: boolean; current: number; target: number; location: string },
		employeeStatusWarning?: EmployeeStatusWarningProps,
	) => {
		const nextState: NurseWarningState = {
			isTargetWarningOpen: false,
			isPtoWarningOpen: false,
			isAgencyWarningOpen: false,
			isOvertimeWarningOpen: false,
			isDailyHoursWarningOpen: false,
			isEmployeeStatusWarningOpen: false,
			overtimeDetails: { hours: overtimeHours || 0, cost: overtimeCost || "$0" },
			dailyHoursDetails: { hours: dailyHours || 0 },
			targetDetails: { current: targetWarning?.current || 0, target: targetWarning?.target || 0, location: targetWarning?.location || "" },
			employeeStatusDetails: employeeStatusWarning ?? null,
		};

		const cascade: PendingCascade = {
			isAgency: !!isAgency,
			overtimeHours: overtimeHours || 0,
			dailyHours: dailyHours || 0,
			isActual: !!isActual,
			hasPto: !!hasPto,
			targetExceeded: !!targetWarning?.exceeded,
		};

		// On Leave / Suspended is the outermost gate — confirm before any staffing checks run.
		if (employeeStatusWarning) {
			pendingRef.current = cascade;
			setWarningState({ ...nextState, isEmployeeStatusWarningOpen: true });
			return;
		}

		proceedToTargetGate(nextState, cascade);
	};

	const handleEmployeeStatusConfirm = () => {
		const pending = pendingRef.current;
		if (!pending) {
			setWarningState((prev) => ({ ...prev, isEmployeeStatusWarningOpen: false }));
			onComplete();
			return;
		}
		proceedToTargetGate({ ...warningState, isEmployeeStatusWarningOpen: false }, pending);
	};

	const handleTargetConfirm = () => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		if (!pending) {
			setWarningState((prev) => ({ ...prev, isTargetWarningOpen: false }));
			onComplete();
			return;
		}
		proceedStandardCascade(warningState, pending);
	};

	const handlePtoConfirm = () => {
		proceedAfterPto(warningState);
	};

	const handleAgencyConfirm = () => {
		setWarningState({
			...warningState,
			isAgencyWarningOpen: false,
		});

		if (warningState.dailyHoursDetails.hours > limit) {
			setWarningState((prev) => ({
				...prev,
				isDailyHoursWarningOpen: true,
				isAgencyWarningOpen: false,
			}));
		} else if (warningState.overtimeDetails.hours > 0) {
			setWarningState((prev) => ({
				...prev,
				isOvertimeWarningOpen: true,
				isAgencyWarningOpen: false,
			}));
		} else {
			onComplete();
		}
	};

	const handleDailyHoursConfirm = () => {
		setWarningState({
			...warningState,
			isDailyHoursWarningOpen: false,
		});

		if (warningState.overtimeDetails.hours > 0) {
			setWarningState((prev) => ({
				...prev,
				isOvertimeWarningOpen: true,
				isDailyHoursWarningOpen: false,
			}));
		} else {
			onComplete();
		}
	};

	const handleOvertimeConfirm = () => {
		setWarningState({
			...warningState,
			isOvertimeWarningOpen: false,
		});

		onComplete();
	};

	const handleCancel = () => {
		pendingRef.current = null;
		setWarningState({
			isTargetWarningOpen: false,
			isPtoWarningOpen: false,
			isAgencyWarningOpen: false,
			isOvertimeWarningOpen: false,
			isDailyHoursWarningOpen: false,
			isEmployeeStatusWarningOpen: false,
			overtimeDetails: { hours: 0, cost: "$0" },
			dailyHoursDetails: { hours: 0 },
			targetDetails: { current: 0, target: 0, location: "" },
			employeeStatusDetails: null,
		});
	};

	return [
		warningState,
		checkWarnings,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancel,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
	];
};
